<script setup>
import { ref } from 'vue'
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import { INTERVAL_OPTIONS, CUSTOM_INTERVAL, CUSTOM_UNITS } from './adScheduleForm'

/**
 * "Every 15 minutes" preset picker with the custom value + unit row that
 * appears when "Custom interval" is chosen.
 */
const props = defineProps({
	interval: { type: String, default: null },
	customValue: { type: [String, Number], default: '' },
	customUnit: { type: String, default: 'Seconds' },
	label: { type: String, default: 'Repeat every' },
	error: { type: String, default: '' },
	customError: { type: String, default: '' },
})
const emit = defineEmits(['update:interval', 'update:customValue', 'update:customUnit', 'blur'])

const presetMenu = ref(null)
const unitMenu = ref(null)

const chooseInterval = (option) => {
	emit('update:interval', option)
	presetMenu.value?.hide()
}
const chooseUnit = (unit) => {
	emit('update:customUnit', unit)
	unitMenu.value?.hide()
}
</script>

<template>
	<div class="flex flex-col gap-3">
		<div class="min-w-0">
			<label class="mb-2 block text-tiny font-medium text-surface-text-1">{{ label }}</label>
			<DropDownMenu ref="presetMenu" type="flat" size="md" fullWidth data-testid="schedule-interval" :aria-label="label">
				<template #toggle-button>
					<span :class="interval ? 'text-surface-text-2' : 'text-surface-6'">{{ interval || 'Select an interval' }}</span>
					<icon-base class="ml-auto shrink-0 text-surface-8"><icon-chevron-down /></icon-base>
				</template>
				<template #menu-items>
					<DropDownMenuItem v-for="option in INTERVAL_OPTIONS" :key="option" @click="chooseInterval(option)">
						<span class="ml-2" :class="option === interval ? 'text-white' : ''">{{ option }}</span>
					</DropDownMenuItem>
				</template>
			</DropDownMenu>
			<p v-if="error" class="mt-1 text-xs text-error">{{ error }}</p>
		</div>

		<div v-if="interval === CUSTOM_INTERVAL" class="flex items-start gap-3">
			<div class="min-w-0 flex-1" data-testid="schedule-interval-custom-value">
				<FormInput
					label="Every"
					type="number"
					min="1"
					placeholder="e.g. 45"
					:model-value="customValue"
					:is-error="!!customError"
					:helper="customError || undefined"
					@update:model-value="emit('update:customValue', $event)"
					@blur="emit('blur', 'customValue')"
				/>
			</div>
			<div class="w-32 shrink-0">
				<label class="mb-2 block text-tiny font-medium text-surface-text-1">Unit</label>
				<DropDownMenu ref="unitMenu" type="flat" size="md" fullWidth data-testid="schedule-interval-custom-unit" aria-label="Interval unit">
					<template #toggle-button>
						<span class="text-surface-text-2">{{ customUnit }}</span>
						<icon-base class="ml-auto shrink-0 text-surface-8"><icon-chevron-down /></icon-base>
					</template>
					<template #menu-items>
						<DropDownMenuItem v-for="unit in CUSTOM_UNITS" :key="unit" @click="chooseUnit(unit)">
							<span class="ml-2">{{ unit }}</span>
						</DropDownMenuItem>
					</template>
				</DropDownMenu>
			</div>
		</div>
	</div>
</template>
